import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IconButton, Menu, MenuItem } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import MLFullScreenDialog from '../../../MLFullScreenDialog/MLFullScreenDialog';
import MLButton from '../../../MLButton/MLButton';
import { redirectToCourseSection } from '../../utils';
import CustomXAxisTick from './CustomXAxisTick';

const BarChartWithThreshold = ({
  data,
  title,
  description,
  threshold,
  thresholdColor = 'red',
  series = [],
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const chartRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const fullscreenScrollContainerRef = useRef(null);

  // Sort data naturally by section label or name
  const sortedData = [...(data.sections || [])].sort((a, b) => {
    const labelA = a.sectionLabel || a.name || '';
    const labelB = b.sectionLabel || b.name || '';
    return labelA.localeCompare(labelB, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });

  // Calculate chart width for many sections
  const getChartWidth = (sectionCount) => {
    if (sectionCount <= 25) {
      return '100%';
    }
    // Calculate minimum width: ~60px per bar
    return Math.max(400, sectionCount * 60);
  };

  const sectionCount = sortedData?.length || 0;
  const labelInterval = 0; // Show all labels
  const chartWidth = getChartWidth(sectionCount);
  const needsScrolling = sectionCount > 25;

  // Reset scroll position to 0 when component mounts or data changes
  useLayoutEffect(() => {
    if (scrollContainerRef.current && needsScrolling) {
      const resetScroll = () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = 0;
        }
      };

      // Immediately set scroll to 0
      resetScroll();

      // Use requestAnimationFrame to ensure it happens after layout
      requestAnimationFrame(() => {
        resetScroll();
        // Also try after a short delay
        setTimeout(resetScroll, 0);
        // And after a longer delay to catch ResponsiveContainer rendering
        setTimeout(resetScroll, 100);
        setTimeout(resetScroll, 300);
      });
    }
  }, [sortedData, needsScrolling, chartWidth]);

  if (sortedData?.length === 0) return null;

  const handleBarClick = (section) => {
    if (
      section.tooltipPosition &&
      chartRef.current &&
      scrollContainerRef.current
    ) {
      const chartRect = chartRef.current.getBoundingClientRect();
      const scrollContainerRect =
        scrollContainerRef.current.getBoundingClientRect();
      // Calculate position relative to scroll container, then add scroll offset
      const relativeX = section.tooltipPosition.x;
      const scrollLeft = scrollContainerRef.current.scrollLeft || 0;
      setMenuAnchor({
        left: scrollContainerRect.left + relativeX - scrollLeft,
        top: chartRect.top + section.tooltipPosition.y,
      });
    } else if (section.tooltipPosition && chartRef.current) {
      // Fallback for non-scrolling case
      const chartRect = chartRef.current.getBoundingClientRect();
      setMenuAnchor({
        left: chartRect.left + section.tooltipPosition.x,
        top: chartRect.top + section.tooltipPosition.y,
      });
    }
    setSelectedSection(section);
  };

  const handleClose = () => {
    setMenuAnchor(null);
    setSelectedSection(null);
  };

  const handleLabelClick = (label) => {
    const sectionData = sortedData.find(
      (d) => d.sectionLabel === label || d.name === label,
    );
    if (sectionData) {
      redirectToCourseSection(
        sectionData.sectionId || sectionData.shortCourseId,
      );
    }
  };

  // Function to render chart content (reusable for normal and fullscreen views)
  const renderChartContent = (isFullscreen = false) => {
    // For fullscreen, use viewport-based heights; for normal view, use fixed heights
    const chartHeight = isFullscreen ? window.innerHeight - 200 : 400;
    const containerHeight = isFullscreen ? window.innerHeight - 180 : 420;
    const fixedYAxisHeight = isFullscreen ? window.innerHeight - 275 : 345;
    const fixedYAxisChartHeight = isFullscreen ? window.innerHeight - 230 : 370;

    // Define Y ticks — include threshold if provided so dotted line aligns with tick grid
    const yTicks = (() => {
      const base = [0, 20, 40, 60, 80, 100];
      if (typeof threshold === 'number' && !Number.isNaN(threshold)) {
        // Ensure threshold exists in ticks list. Must re-sort or Recharts may omit ticks
        if (!base.includes(threshold)) {
          return [0, 20, 40, 60, 80, 100, threshold].sort((a, b) => a - b);
        }
        return base;
      }
      return base;
    })();

    return (
      <div
        style={{
          width: '100%',
          position: 'relative',
          display: needsScrolling ? 'flex' : 'block',
          overflowY: 'hidden',
          paddingBottom: '0px',
          paddingTop: '0px',
          height: `${containerHeight}px`,
        }}
      >
        {/* Fixed Y-axis - only when scrolling */}
        {needsScrolling && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              zIndex: 10,
              backgroundColor: 'white',
              width: '60px',
              height: `${fixedYAxisHeight}px`,
              pointerEvents: 'none',
            }}
          >
            <BarChart
              width={60}
              height={fixedYAxisChartHeight}
              data={sortedData}
              barCategoryGap="20%"
              barGap={5}
              margin={{ left: 0, right: 20, top: 5, bottom: 52 }}
            >
              <XAxis
                hide
                dataKey="sectionLabel"
                angle={-35}
                textAnchor="end"
                interval={labelInterval}
                padding={{ left: 20, right: 20 }}
              />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(tick) => `${tick}%`}
                ticks={yTicks}
                width={60}
                orientation="left"
                allowDecimals={false}
              />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend content={() => null} />
              {typeof threshold === 'number' && (
                <ReferenceLine
                  y={threshold}
                  stroke={thresholdColor}
                  strokeDasharray="4 4"
                />
              )}
              <Bar
                dataKey={series[0]?.dataKey}
                fill="transparent"
                barSize={30}
              />
            </BarChart>
          </div>
        )}
        {/* Chart content */}
        <div
          ref={isFullscreen ? fullscreenScrollContainerRef : scrollContainerRef}
          style={{
            width: '100%',
            overflowX: needsScrolling ? 'auto' : 'visible',
            overflowY: 'hidden',
            position: 'relative',
            direction: 'ltr',
            WebkitOverflowScrolling: needsScrolling ? 'touch' : 'auto',
            scrollbarWidth: needsScrolling ? 'thin' : 'auto',
            marginLeft: needsScrolling ? '60px' : 0,
            marginTop: 0,
            marginBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: 0,
            height: `${chartHeight}px`,
          }}
        >
          <div style={{ margin: 0, padding: 0, display: 'block' }}>
            {needsScrolling ? (
              <BarChart
                width={chartWidth}
                height={chartHeight}
                data={sortedData}
                barCategoryGap="20%"
                barGap={5}
                margin={{ left: 0, right: 20, top: 5, bottom: 52 }}
              >
                <XAxis
                  dataKey="sectionLabel"
                  angle={-35}
                  textAnchor="end"
                  interval={labelInterval}
                  padding={{ left: 20, right: 20 }}
                  tick={(props) => (
                    <CustomXAxisTick
                      {...props}
                      handleLabelClick={handleLabelClick}
                    />
                  )}
                />
                <YAxis
                  hide
                  domain={[0, 100]}
                  tickFormatter={(tick) => `${tick}%`}
                  ticks={[0, 20, 40, threshold, 80, 100]}
                  width={60}
                  allowDecimals={false}
                />
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend content={() => null} />
                {typeof threshold === 'number' && (
                  <ReferenceLine
                    y={threshold}
                    stroke={thresholdColor}
                    strokeDasharray="4 4"
                  />
                )}
                {series.map((s) => (
                  <Bar
                    key={s.dataKey}
                    dataKey={s.dataKey}
                    fill={s.color}
                    name={s.name}
                    barSize={30}
                    stackId={s.stackId}
                    onMouseUp={handleBarClick}
                  />
                ))}
              </BarChart>
            ) : (
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                  data={sortedData}
                  barCategoryGap="20%"
                  barGap={5}
                  margin={{ left: 0, right: 20, top: 5, bottom: 52 }}
                >
                  <XAxis
                    dataKey="sectionLabel"
                    angle={-35}
                    textAnchor="end"
                    interval={labelInterval}
                    padding={{ left: 20, right: 20 }}
                    tick={(props) => (
                      <CustomXAxisTick
                        {...props}
                        handleLabelClick={handleLabelClick}
                      />
                    )}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tickFormatter={(tick) => `${tick}%`}
                    ticks={yTicks}
                    width={60}
                    allowDecimals={false}
                  />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend content={() => null} />
                  {typeof threshold === 'number' && (
                    <ReferenceLine
                      y={threshold}
                      stroke={thresholdColor}
                      strokeDasharray="4 4"
                    />
                  )}
                  {series.map((s, idx) => (
                    <Bar
                      key={s.dataKey || idx}
                      dataKey={s.dataKey}
                      fill={s.color}
                      name={s.name}
                      barSize={30}
                      stackId={s.stackId}
                      onMouseUp={handleBarClick}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        {/* Legend outside scrollable area */}
        <div
          style={{
            position: 'absolute',
            bottom: '0',
            left: '60px',
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            {series.map((s, idx) => (
              <div
                key={s.dataKey || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    backgroundColor: s.color,
                    borderRadius: '2px',
                  }}
                />
                <span style={{ fontSize: '14px', color: '#666' }}>
                  {s.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        ref={chartRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: needsScrolling ? 'flex-start' : 'center',
          minWidth: '400px',
          width: '100%',
          overflowY: 'hidden',
        }}
      >
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <h3 style={{ margin: 0 }}>{title}</h3>
            <IconButton
              size="small"
              onClick={() => setIsFullscreenOpen(true)}
              aria-label="Expand chart to fullscreen"
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <FullscreenIcon />
            </IconButton>
          </div>

          {description && (
            <p
              style={{
                fontSize: '14px',
                color: 'gray',
                textAlign: 'center',
                maxWidth: '80%',
                marginBottom: '10px',
                marginTop: '10px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>

        {renderChartContent(false)}

        <Menu
          anchorReference="anchorPosition"
          anchorPosition={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleClose}
        >
          {selectedSection && [
            <MenuItem key="title" disabled>
              {selectedSection.name}
            </MenuItem>,
            <MenuItem
              key="view"
              onClick={() =>
                redirectToCourseSection(
                  selectedSection.sectionId || selectedSection.shortCourseId,
                )
              }
            >
              <OpenInNewIcon /> View Section Details
            </MenuItem>,
          ]}
        </Menu>
      </div>

      <MLFullScreenDialog
        open={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        title={title}
        actions={
          <MLButton
            btnType="secondary"
            onClick={() => setIsFullscreenOpen(false)}
            ariaLabel="Close fullscreen"
          >
            <CloseIcon style={{ marginRight: '8px' }} />
            Close
          </MLButton>
        }
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: needsScrolling ? 'flex-start' : 'center',
            width: '100%',
            padding: '20px',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            backgroundColor: '#fff',
          }}
        >
          {description && (
            <p
              style={{
                fontSize: '14px',
                color: 'gray',
                textAlign: 'center',
                maxWidth: '80%',
                marginBottom: '20px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          {renderChartContent(true)}
        </div>
      </MLFullScreenDialog>
    </>
  );
};

export default BarChartWithThreshold;

BarChartWithThreshold.propTypes = {
  data: PropTypes.shape({
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        sectionLabel: PropTypes.string,
        name: PropTypes.string,
        sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        shortCourseId: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
      }),
    ),
  }).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  threshold: PropTypes.number,
  thresholdColor: PropTypes.string,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      stackId: PropTypes.string,
    }),
  ),
};
